import {
	createTheme,
	CSSVariablesResolver,
	DEFAULT_THEME,
	mergeMantineTheme,
	rem,
} from '@mantine/core';

import './layout.css';

import { COLORS } from './colors';

export const resolver: CSSVariablesResolver = (
	theme
) => ({
	variables: {},
	light: {
		'--mantine-color-flame-red': theme.other.flameRed,
		'--mantine-color-turquoise': theme.other.turquoise,
		'--mantine-color-mediumpurple':
			theme.other.mediumpurple,
		'--mantine-color-success-primary':
			theme.other.success.primary,
		'--mantine-color-warning-primary':
			theme.other.warning.primary,
		'--mantine-color-grey': theme.other.grey,
		'--mantine-color-grey-clair': theme.other.greyClair,
		'--mantine-color-light-grey': theme.other.lightGrey,
		'--mantine-color-blue': theme.other.blue,
		'--mantine-color-light-blue': theme.other.lightBlue,
		'--mantine-color-vivid-sky-blue':
			theme.other.vividSkyBlue,
		'--mantine-color-black': theme.other.black,
		'--mantine-color-dark-blue': theme.other.darkBlue,
		'--mantine-color-bright-blue': theme.other.brightBlue,
		'--mantine-color-sky-blue': theme.other.skyBlue,
		'--mantine-color-off-white-blue':
			theme.other.offWhiteBlue,
		'--mantine-color-error': theme.other.error,
		'--mantine-color-coral-red': theme.other.coralRed,
		'--mantine-color-scroll-primary':
			theme.other.scroll.primary,
		'--mantine-color-background-primary':
			theme.other.background.primary,
		'--mantine-color-background-secondary':
			theme.other.background.secondary,
		'--mantine-color-background-tertiary':
			theme.other.background.tertiary,
		'--mantine-color-background-content':
			theme.other.background.content,
	},
	dark: {
		'--mantine-color-primary': theme.other.primary,
		'--mantine-color-secondary': theme.other.secondary,
	},
});

const themeOverride = createTheme({
	other: COLORS,
	cursorType: 'pointer',
	fontFamily: 'Montserrat, sans-serif',
	headings: {
		fontFamily: 'Montserrat, sans-serif',
		sizes: {
			h1: {
				fontWeight: '800',
				fontSize: rem(20),
				lineHeight: '25px',
			},
			h2: {
				fontWeight: '800',
				fontSize: rem(28),
				lineHeight: '30px',
			},
			h3: {
				fontWeight: '700',
				fontSize: rem(18),
				lineHeight: '20px',
			},
			h4: {
				fontWeight: '700',
				fontSize: rem(16),
				lineHeight: '20px',
			},
			h5: {
				fontWeight: '700',
				fontSize: rem(14),
				lineHeight: '16px',
			},
			h6: {
				fontWeight: '700',
				fontSize: rem(12),
				lineHeight: '16px',
			},
		},
	},
	breakpoints: {
		xs: '36em',
		sm: '48em',
		md: '62em',
		lg: '75em',
		xl: '88em',
		xxl: '120em',
	},
	primaryShade: 9,

	colors: {
		success: [
			'#05130C',
			'#0A2519',
			'#0F3825',
			'#144A32',
			'#195D3E',
			'#1E704A',
			'#238257',
			'#289563',
			'#2DA770',
			'#32BA7C',
		],
		alert: [
			'#191007',
			'#331F0E',
			'#4C2F15',
			'#663F1C',
			'#804F24',
			'#995E2B',
			'#B36E32',
			'#CC7E39',
			'#E68D40',
			'#FF9D47',
		],
		error: [
			'#190A09',
			'#311412',
			'#4A1E1B',
			'#622824',
			'#7B322D',
			'#943B36',
			'#AC453F',
			'#C54F48',
			'#DD5951',
			'#F6635A',
		],
		'inputs-texts-icons': [
			'#0C0C11',
			'#181922',
			'#232532',
			'#2F3243',
			'#3B3E54',
			'#474A65',
			'#535776',
			'#5E6386',
			'#6A7097',
			'#767CA8',
		],
		'neutral-light': [
			'#181819',
			'#303133',
			'#48494C',
			'#606165',
			'#787A7F',
			'#909298',
			'#A8AAB1',
			'#C0C2CA',
			'#D8DBE4',
			'#F0F3FD',
		],
		'background-color': [
			'#181819',
			'#313132',
			'#49494B',
			'#626264',
			'#7B7B7D',
			'#939396',
			'#ACACAf',
			'#C4C4C8',
			'#DDDDE1',
			'#F5F5FA',
		],
		'neutral-dark': [
			'#040405',
			'#08090A',
			'#0C0D0E',
			'#101213',
			'#151718',
			'#191B1D',
			'#1D1F22',
			'#212426',
			'#25292B',
			'#292D30',
		],
		'blue-dark': [
			'#010407',
			'#03080F',
			'#040C16',
			'#06101E',
			'#081426',
			'#09182D',
			'#0B1C35',
			'#0C203C',
			'#0E2444',
			'#0F284B',
		],
		blue: [
			'#0A0D17',
			'#14192E',
			'#1E2646',
			'#28335D',
			'#324074',
			'#3C4C8B',
			'#4659A2',
			'#5066BA',
			'#5A72D1',
			'#647FE8',
			'#D2DBF6',
		],
		orange: [
			'#fff4e2',
			'#ffe9cc',
			'#ffd09c',
			'#fdb766',
			'#fca13a',
			'#fb931d',
			'#fc8c0c',
			'#e17900',
			'#c86a00',
			'#ae5a00',
		],
		cyan: [
			'#7AD1DD',
			'#5FCCDB',
			'#44CADC',
			'#2AC9DE',
			'#1AC2D9',
			'#11B7CD',
			'#09ADC3',
			'#0E99AC',
			'#128797',
			'#147885',
		],
		purple: [
			'#F3F0FF',
			'#E5DBFF',
			'#D0BFFF',
			'#B197FC',
			'#9775FA',
			'#845EF7',
			'#7950F2',
			'#7048E8',
			'#6741D9',
			'#5F3DC4',
		],
		indigo: [
			'#EDF2FF',
			'#DBE4FF',
			'#BAC8FF',
			'#91A7FF',
			'#748FFC',
			'#5C7CFA',
			'#4C6EF5',
			'#4263EB',
			'#3B5BDB',
			'#364FC7',
		],
		green: [
			'#EBFBEE',
			'#D3F9D8',
			'#B2F2BB',
			'#8CE99A',
			'#69DB7C',
			'#51CF66',
			'#40C057',
			'#37B24D',
			'#2F9E44',
			'#2B8A3E',
		],
	},
	focusRing: 'never',
	primaryColor: 'blue',
});

export const theme = mergeMantineTheme(
	DEFAULT_THEME,
	themeOverride
);
